<template>
  <div class="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 to-slate-100 relative overflow-hidden">
    <!-- Decorative background elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-oxo-red/5 blur-3xl"></div>
      <div class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-oxo-yellow/10 blur-3xl"></div>
    </div>

    <div class="z-10 w-full max-w-md p-6">
      <div class="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-white/50">
        
        <div class="text-center mb-8">
          <div class="relative inline-block">
            <div class="relative rounded-2xl bg-white p-4">
              <span class="text-4xl font-bold text-oxo-red">OXO</span>
            </div>
          </div>

          <h2 class="mt-6 text-2xl font-display font-bold text-slate-800 tracking-tight">
            Mot de passe oublié
          </h2>
          <p class="mt-2 text-sm text-slate-500">
            Entrez votre adresse email pour recevoir un lien de réinitialisation
          </p>
        </div>

        <form v-if="!success" class="space-y-5" @submit.prevent="handleSubmit">
          
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1.5 ml-1">Email</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-oxo-red transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input 
                v-model="email" 
                type="email" 
                required
                class="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:ring-2 focus:ring-oxo-red/20 focus:border-oxo-red transition-all duration-200 sm:text-sm"
                placeholder="Votre adresse email"
              />
            </div>
          </div>

          <transition name="fade">
            <div v-if="error" class="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-red-700 font-medium">{{ error }}</span>
            </div>
          </transition>

          <button 
            type="submit" 
            :disabled="loading" 
            class="w-full relative flex justify-center py-3 px-4 border border-transparent rounded-xl text-sm font-bold text-white bg-linear-to-r from-oxo-red to-oxo-orange hover:from-red-700 hover:to-orange-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-oxo-red disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span>{{ loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation' }}</span>
          </button>
        </form>

        <!-- Success state -->
        <div v-else class="text-center space-y-4">
          <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-lg font-bold text-slate-800">Email envoyé !</h3>
          <p class="text-sm text-slate-500">
            Si un compte existe avec cet email, vous recevrez un lien de réinitialisation dans quelques instants.
          </p>
          <p class="text-xs text-slate-400">
            Pensez à vérifier vos spams.
          </p>
        </div>

        <div class="mt-6 text-center">
          <NuxtLink to="/auth/login" class="text-sm text-oxo-red hover:text-oxo-orange transition-colors">
            ← Retour à la connexion
          </NuxtLink>
        </div>
        
        <div class="mt-8 text-center text-xs text-slate-400">
           &copy; {{ new Date().getFullYear() }} OXONET par l'École OXO. Tous droits réservés.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: false
})

const config = useRuntimeConfig()
const apiBase = config.public.apiBase

const email = ref('')
const loading = ref(false)
const error = ref('')
const success = ref(false)

const handleSubmit = async () => {
  loading.value = true
  error.value = ''
  try {
    await $fetch(`${apiBase}/auth/forgot-password`, {
      method: 'POST',
      body: { email: email.value }
    })
    success.value = true
  } catch (e: any) {
    // Even on error, show success to prevent email enumeration
    success.value = true
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
